import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const OnboardingProfile = () => {
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  
  // Pre-filled stock affirmations
  const stockAffirmations = [
    "I am worthy of love and abundance in all areas of my life",
    "I trust the wisdom of my inner voice and intuition", 
    "I am a powerful creator of my own reality",
    "Love flows through me and surrounds me always",
    "I am grateful for the endless possibilities before me",
    "My authentic self shines brightly and inspires others",
    "I embrace change as an opportunity for growth and expansion",
    "Peace and harmony flow through every aspect of my being",
    "I am connected to the infinite source of creativity within me",
    "I attract positive experiences and loving relationships"
  ];

  const [formData, setFormData] = useState({
    affirmations: [...stockAffirmations],
    affirmationImages: new Array(40).fill(null),
    bio: '',
    location: '',
    secretMysteryName: ''
  });

  const [uploadingImages, setUploadingImages] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleAffirmationChange = (index, value) => {
    const newAffirmations = [...formData.affirmations];
    newAffirmations[index] = value;
    setFormData(prev => ({
      ...prev,
      affirmations: newAffirmations
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const uploadImage = async (file, index) => {
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    formDataUpload.append('upload_type', 'affirmation_image');
    formDataUpload.append('index', index.toString());

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formDataUpload
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      return data.image_url;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleImageUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setErrors(prev => ({
        ...prev,
        [`image_${index}`]: 'Image must be smaller than 10MB'
      }));
      return;
    }

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({
        ...prev,
        [`image_${index}`]: 'Please select an image file'
      }));
      return;
    }

    setUploadingImages(prev => new Set([...prev, index]));
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`image_${index}`];
      return newErrors;
    });

    try {
      const imageUrl = await uploadImage(file, index);
      const newImages = [...formData.affirmationImages];
      newImages[index] = imageUrl;
      
      setFormData(prev => ({
        ...prev,
        affirmationImages: newImages
      }));
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        [`image_${index}`]: 'Upload failed. Please try again.'
      }));
    } finally {
      setUploadingImages(prev => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = [...formData.affirmationImages];
    newImages[index] = null;
    setFormData(prev => ({
      ...prev,
      affirmationImages: newImages
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await updateProfile({
        affirmations: formData.affirmations,
        affirmation_images: formData.affirmationImages,
        bio: formData.bio,
        location: formData.location,
        secret_mystery_name: formData.secretMysteryName || `${user?.username}_spirit`
      });

      // Navigate to main feed after successful profile setup
      navigate('/dashboard');
    } catch (error) {
      setErrors({
        submit: error.message || 'Profile setup failed. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Set Up Your Profile ✨
          </h1>
          <p className="text-xl text-blue-200">
            Customize your affirmations and create your vision board
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* Basic Profile Information */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Basic Information</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Bio (Optional)
                </label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                  placeholder="Tell the community a bit about yourself..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Location (Optional)
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  placeholder="e.g., San Francisco, CA"
                />
                
                <label className="block text-sm font-medium text-blue-200 mb-2 mt-4">
                  Mystery Name (Optional)
                </label>
                <input
                  type="text"
                  name="secretMysteryName"
                  value={formData.secretMysteryName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                  placeholder="Your anonymous community identity"
                />
              </div>
            </div>
          </div>

          {/* Affirmations Section */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Your Daily Affirmations</h2>
            <p className="text-blue-200 mb-6">
              We've pre-filled these with positive affirmations to get you started. 
              Feel free to customize them to match your personal goals and values.
            </p>
            
            <div className="space-y-4">
              {formData.affirmations.map((affirmation, index) => (
                <div key={index}>
                  <label className="block text-sm font-medium text-blue-200 mb-2">
                    Affirmation {index + 1}
                  </label>
                  <textarea
                    value={affirmation}
                    onChange={(e) => handleAffirmationChange(index, e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent resize-none"
                    rows={2}
                    placeholder={`Enter your ${index + 1}${index === 0 ? 'st' : index === 1 ? 'nd' : index === 2 ? 'rd' : 'th'} affirmation...`}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Vision Board Section */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Your Vision Board</h2>
            <p className="text-blue-200 mb-6">
              Upload up to 40 images that represent your goals, dreams, and aspirations. 
              These will be displayed during your daily morning protocol.
            </p>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-8 gap-4">
              {formData.affirmationImages.map((image, index) => (
                <div key={index} className="relative aspect-square">
                  {image ? (
                    <div className="group relative w-full h-full">
                      <img
                        src={image}
                        alt={`Vision ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-full border-2 border-dashed border-gray-400 rounded-lg cursor-pointer bg-white/5 hover:bg-white/10 transition-colors">
                      {uploadingImages.has(index) ? (
                        <div className="text-blue-400">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400"></div>
                        </div>
                      ) : (
                        <>
                          <svg className="w-6 h-6 text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <span className="text-xs text-gray-400">{index + 1}</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, index)}
                        className="hidden"
                      />
                    </label>
                  )}
                  
                  {errors[`image_${index}`] && (
                    <p className="text-xs text-red-400 mt-1">{errors[`image_${index}`]}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Submit Section */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8">
            {errors.submit && (
              <div className="bg-red-500/20 border border-red-400 rounded-xl p-4 mb-6">
                <p className="text-red-400 text-center">{errors.submit}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || uploadingImages.size > 0}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 text-white font-bold py-4 px-8 rounded-xl transition-all duration-200 transform hover:scale-105"
            >
              {isLoading ? 'Saving Your Profile...' : 
               uploadingImages.size > 0 ? `Uploading ${uploadingImages.size} image${uploadingImages.size > 1 ? 's' : ''}...` :
               'Complete Setup & Enter Platform →'}
            </button>

            <p className="text-center text-blue-200 mt-4">
              You can always modify your affirmations and vision board later in your profile settings.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OnboardingProfile;